import moment from 'moment';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Chip from '@mui/material/Chip';
import {
  Card,
  Grid,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { getAutomationLogList } from 'src/server/store/automation';

import { useTable } from 'src/components/table';
import Scrollbar from 'src/components/scrollbar';
import EmptyContent from 'src/components/empty-content';
import TableSelectedAction from 'src/components/table/table-selected-action';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import AutomationTableHeadCustom from 'src/components/table/automation-table-head-custom';

import AutomationLogFilters from './AutomationLogFilters';
import AutomationLogErrorDialog from './AutomationLogErrorDialog';

// Utility function to truncate text
// function truncateText(text, maxLength = 50) {
//   if (!text) return '';
//   return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
// }

const TABLE_HEAD = [
  { id: 'no', label: '#' },
  // { id: 'title', label: 'Log Name' },
  { id: 'automationName', label: 'Automation' },
  { id: 'findings', label: 'Findings Count' },
  { id: 'ruleTemplate', label: 'Rule Template' },
  { id: 'executionType', label: 'Execution Type' },
  { id: 'status', label: 'Status' },
  { id: 'createdAt', label: 'Run Time' },
  // { id: 'summary', label: 'Summary' },
  // { id: 'error', label: 'Error' },
  { id: 'completedAt', label: 'Completed At' },
];

export default function AutomationLogs({ formattedDate }) {
  const dispatch = useDispatch();

  const table = useTable({ defaultOrderBy: 'createdAt', defaultRowsPerPage: 25 });

  const [searchQuery, setSearchQuery] = useState('');
  const [filterObject, setFilterObject] = useState();
  const [sortValue, setSortValue] = useState(-1);
  const [viewOpen, setViewOpen] = useState({
    status: false,
    data: '',
  });

  // Get automation log data from Redux store
  const { automationLogList, loading } = useSelector((state) => ({
    automationLogList: state.automation.automationLogList || [],
    loading: state.automation.loading,
  }));

  // Use mapped data
  const automationLogs = (automationLogList?.automationLogs || []).map((item, idx) => ({
    id: item._id || idx,
    title: item.title || '',
    automationName: item.automationName || '',
    ruleTemplate: item.automationRuleTemplate?.title || '',
    executionType: item.executionType || '',
    status: item.status || '',
    completedAt: item.completedAt ? new Date(item.completedAt) : '',
    summary: item.summary || '',
    error: item.error || '',
    createdAt: item.createdAt ? new Date(item.createdAt) : '',
    raw: item,
  }));

  useEffect(() => {
    const filterDataObj = {
      filter_data: filterObject,
      sort_data: {
        createdAt: sortValue,
      },
    };

    const credentials = {
      state: filterDataObj,
      page: table?.page,
      limit: table?.rowsPerPage || 25,
      search: searchQuery,
      dispatch,
      date: formattedDate,
    };

    dispatch(
      getAutomationLogList({
        credentials,
      })
    );
  }, [
    dispatch,
    filterObject,
    sortValue,
    searchQuery,
    table?.page,
    table?.rowsPerPage,
    formattedDate,
  ]);

  const totalPage = automationLogList?.total;

  // Helper for status chip color
  function getStatusColor(status) {
    if (status === 'PENDING') return 'info';
    if (status === 'SUCCESS') return 'success';
    if (status === 'FAILED') return 'error';
    return 'default';
  }

  return (
    <Card sx={{ padding: '10px 14px 14px' }}>
      <Grid>
        <AutomationLogFilters
          getSearchPlaceholder={() => 'Search Automation, Rule'}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setFilterObject={setFilterObject}
          sortValue={sortValue}
          setSortValue={setSortValue}
          sort={null}
        />
      </Grid>
      <Stack spacing={3} pt={3}>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <TableSelectedAction
            dense={table.dense}
            numSelected={table.selected?.length}
            rowCount={automationLogList?.total}
            onSelectAllRows={(checked) => {
              table.onSelectAllRows(
                checked,
                automationLogs.map((row) => row.id)
              );
            }}
          />
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'}>
              <AutomationTableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                rowCount={automationLogList?.total}
                numSelected={table.selected?.length}
                onSort={table.onSort}
              />
              {loading ? (
                <TableRow>
                  <TableCell align="center" colSpan={12} sx={{ py: 3 }}>
                    <Paper sx={{ textAlign: 'center' }}>
                      <CircularProgress color="inherit" />
                    </Paper>
                  </TableCell>
                </TableRow>
              ) : (
                <TableBody>
                  {automationLogs?.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.automationName}</TableCell>
                      <TableCell>
                        {/* {row.title}{' '} */}
                        {/* <Tooltip title="Automation Findings Count"> */}
                        <Chip
                          label={row.raw?.automationFindingsCount}
                          color="primary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                        {/* </Tooltip> */}
                      </TableCell>
                      <TableCell>{row.ruleTemplate}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.executionType}
                          color={row.executionType === 'AUTOMATIC' ? 'success' : 'default'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() =>
                            setViewOpen({
                              status: true,
                              data: row.error ? row.error.message || row.error : '',
                            })
                          }
                        >
                          <Chip
                            label={row.status}
                            color={getStatusColor(row.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                            variant="outlined"
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={400}>
                          {row.createdAt
                            ? moment(row.createdAt).format('DD MMM YYYY hh:mm:ss a')
                            : '--'}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        <Tooltip title={row.summary || ''} placement="top" arrow>
                          <span>{truncateText(row.summary, 60)}</span>
                        </Tooltip> */}
                      {/* </TableCell> */}
                      {/* <TableCell>
                        <TextMaxLine line={3}>
                          <Typography variant="caption" fontWeight={400}>
                            {row.error ? row.error.message || row.error : '--'}
                          </Typography>
                        </TextMaxLine>
                      </TableCell> */}
                      <TableCell>
                        <Typography variant="caption" fontWeight={400}>
                          {row.completedAt
                            ? moment(row.completedAt).format('DD MMM YYYY hh:mm:ss a')
                            : '--'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </Scrollbar>
          {totalPage === 0 && (
            <Stack sx={{ p: 2, textAlign: 'center' }}>
              <EmptyContent
                filled
                title=""
                sx={{
                  py: 10,
                  width: '100%',
                }}
              />
            </Stack>
          )}
        </TableContainer>

        <TablePaginationCustom
          count={totalPage || 0}
          page={table.page}
          customRowsPerPage={25}
          rowsPerPage={table.rowsPerPage || 25}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense
        />
      </Stack>
      {/* <SingleViewDialog open={viewOpen} onClose={() => setViewOpen(false)} type="automation" /> */}
      <AutomationLogErrorDialog
        open={viewOpen.status}
        onClose={() => setViewOpen({ status: false, data: '' })}
        data={viewOpen.data}
      />
    </Card>
  );
}

AutomationLogs.propTypes = {
  formattedDate: PropTypes.any,
};
